import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Bookmark, Plus, Check } from "lucide-react";

export function SaveButton({ projectId, variant = "icon" }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        onClick={(e) => {
          e.preventDefault(); e.stopPropagation();
          if (!user) return toast.error("Log in to save projects");
          setOpen(true);
        }}
        className={variant === "icon"
          ? "absolute top-2 right-2 z-10 bg-black/60 hover:bg-black/80 text-white rounded-full p-2 opacity-0 group-hover:opacity-100 transition-opacity"
          : "inline-flex items-center gap-2 border border-border rounded-md px-3 py-2 text-sm hover:bg-secondary"
        }
        data-testid={`save-btn-${projectId}`}
        aria-label="Save to collection"
      >
        <Bookmark className="h-4 w-4" />
        {variant !== "icon" && <span>Save</span>}
      </button>
      {open && <SaveDialog projectId={projectId} open={open} onClose={() => setOpen(false)} />}
    </>
  );
}

function SaveDialog({ projectId, open, onClose }) {
  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [newTitle, setNewTitle] = useState("");
  const [isPublic, setIsPublic] = useState(true);
  const [membership, setMembership] = useState({}); // {col_id: bool}

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/me/collections");
      setCollections(data);
      // membership derived from project_ids
      const m = {};
      data.forEach((c) => { m[c.collection_id] = (c.project_ids || []).includes(projectId); });
      setMembership(m);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const toggle = async (c) => {
    const { data } = await api.post(`/collections/${c.collection_id}/toggle/${projectId}`);
    setMembership((m) => ({ ...m, [c.collection_id]: data.added }));
    toast.success(data.added ? `Saved to "${c.title}"` : `Removed from "${c.title}"`);
  };

  const create = async () => {
    if (!newTitle.trim()) return;
    setCreating(true);
    try {
      const { data } = await api.post("/collections", { title: newTitle.trim(), is_public: isPublic });
      await api.post(`/collections/${data.collection_id}/toggle/${projectId}`);
      setNewTitle("");
      toast.success(`Created "${data.title}" and saved`);
      load();
    } finally { setCreating(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="save-dialog">
        <DialogHeader>
          <DialogTitle>Save to collection</DialogTitle>
          <DialogDescription>Group work you love into shareable boards.</DialogDescription>
        </DialogHeader>
        <div className="space-y-2 max-h-64 overflow-y-auto">
          {loading && <div className="text-sm text-muted-foreground">Loading…</div>}
          {!loading && collections.length === 0 && (
            <div className="text-sm text-muted-foreground">No collections yet. Create your first below.</div>
          )}
          {collections.map((c) => (
            <button
              key={c.collection_id}
              onClick={() => toggle(c)}
              className="w-full flex items-center justify-between gap-3 border border-border/60 rounded-md p-3 hover:bg-secondary text-left"
              data-testid={`col-item-${c.collection_id}`}
            >
              <div>
                <div className="font-semibold text-sm">{c.title}</div>
                <div className="meta-label mt-0.5">{c.project_count} works · {c.is_public ? "Public" : "Private"}</div>
              </div>
              {membership[c.collection_id] && <Check className="h-4 w-4 text-[hsl(var(--accent))]" />}
            </button>
          ))}
        </div>
        <div className="pt-4 border-t border-border/60 space-y-3">
          <Label>Create new collection</Label>
          <div className="flex gap-2">
            <Input placeholder="e.g. Mood board" value={newTitle} onChange={(e) => setNewTitle(e.target.value)} data-testid="new-col-title" />
            <Button onClick={create} disabled={creating || !newTitle.trim()} className="bg-[hsl(var(--accent))] hover:bg-[hsl(15_100%_55%)] text-white" data-testid="new-col-create">
              <Plus className="h-4 w-4 mr-1" /> Create
            </Button>
          </div>
          <label className="flex items-center gap-2 text-sm text-muted-foreground">
            <Checkbox checked={isPublic} onCheckedChange={(v) => setIsPublic(!!v)} data-testid="new-col-public" /> Public (shareable link)
          </label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Done</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
